import { useMutation } from "@tanstack/react-query";
import {
  CreateQuoteAddressDto,
  FreightLineItemDto,
  QuotesClient,
  QuoteTaxRequestDto,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";

export interface TaxCheckItem {
  productDescriptor: string;
  productClass: string;
  unitPrice: number;
  quantity: number;
  /** Cart line the item came from; a saved-quote cart line is then taxed from the cart, not this price. */
  sourceCartLineNumber?: number;
  /** The saved-quote item the user reviewed on that cart line; the server rejects the request if the cart line changed. */
  sourceQuoteItemDescriptor?: string;
}

export interface CalculateTaxVariables {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  freight: number;
  items: TaxCheckItem[];
}

// Mirrors useGetShippingMethods: callers pass the same line shape freight uses (productId sent as
// productDescriptor). Catalog lines send only price/qty; a saved-quote cart line (sourceCartLineNumber)
// is taxed server-side from the cart's quoted price, media tag, and UNSPSC.
// Returns the estimated tax amount (coalesced to 0 when the downstream service returns none).
export default function useCalculateTax() {
  const api = useApi(QuotesClient);

  return useMutation<number, Error, CalculateTaxVariables>({
    mutationFn: async (vars) => {
      const result = await api.calculateTax(
        new QuoteTaxRequestDto({
          shipTo: new CreateQuoteAddressDto({
            street: vars.street,
            city: vars.city,
            stateProvince: vars.state,
            postalCode: vars.postalCode,
            country: vars.countryCode,
          }),
          freight: vars.freight,
          items: vars.items.map(
            (item) =>
              new FreightLineItemDto({
                productDescriptor: item.productDescriptor,
                productClass: item.productClass,
                unitPrice: item.unitPrice,
                quantity: item.quantity,
                sourceCartLineNumber: item.sourceCartLineNumber,
                sourceQuoteItemDescriptor: item.sourceQuoteItemDescriptor,
              }),
          ),
        }),
      );
      return result.tax ?? 0;
    },
  });
}
